import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FetchMasterGiftAction } from "../../redux/actions/GiftAction";
import { DashboardLayout } from "../../Pages/dashboard/DashboardLayout";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaGift,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaPlus,
  FaSpinner,
} from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import SearchBar from "../../Pages/Components/SearchBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import axiosInstance from "../../api/axiosInstance";

const GiftList = () => {
  const dispatch = useDispatch();
  const { loading, GiftList, error } = useSelector((state) => state.gift);
  const { userInfo } = useSelector((state) => state.auth);
  const dashboardData = useSelector((state) => state.dashboard?.data);

  const orderType = (
    dashboardData?.user_order_type ||
    userInfo?.user_order_type ||
    userInfo?.data?.user_order_type ||
    "loyality"
  ).toLowerCase();

  const isLoyaltyUser = orderType === "loyality";
  const roleId = useSelector((state) => state.dashboard?.data?.role_id);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [expandedScheme, setExpandedScheme] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const BASE_URL = axiosInstance.defaults.baseURL;

  // Check if user has permission to edit (role_id is 1)
  const canEdit = roleId === "1" || roleId === 1;

  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(FetchMasterGiftAction(userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token]);

  if (error) return <p>Error: {error}</p>;

  const schemeData = Array.isArray(GiftList) ? GiftList : GiftList?.data || [];

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;

    const separator =
      BASE_URL.endsWith("/") || imagePath.startsWith("/") ? "" : "/";
    return `${BASE_URL}${separator}${
      imagePath.startsWith("/") ? imagePath.slice(1) : imagePath
    }`;
  };

  const filteredSchemes = schemeData
    .filter((scheme) => {
      const search = searchValue.toLowerCase();
      const schemeName = scheme.scheme_name?.toLowerCase() || "";
      const createdBy = scheme.gifts?.[0]?.created_by?.toLowerCase() || "";
      return schemeName.includes(search) || createdBy.includes(search);
    })
    .filter((scheme) => {
      if (!startDate && !endDate) return true;

      const rawDate = scheme.created_at?.trim();
      let createdDate;

      if (rawDate.includes("-")) {
        const parts = rawDate.split(" ")[0].split("-");
        if (parts[0].length === 4) {
          createdDate = dayjs(rawDate);
        } else {
          const [day, month, year] = parts;
          const formattedDate = `${year}-${month}-${day}`;
          createdDate = dayjs(formattedDate);
        }
      } else {
        createdDate = dayjs(rawDate);
      }

      if (!createdDate.isValid()) {
        console.warn("Parsed invalid date:", rawDate);
        return false;
      }

      const start = startDate ? dayjs(startDate).startOf("day") : null;
      const end = endDate ? dayjs(endDate).endOf("day") : null;

      if (start && createdDate.isBefore(start)) return false;
      if (end && createdDate.isAfter(end)) return false;

      return true;
    })
    .sort((a, b) => {
      const dateA = dayjs(a.created_at);
      const dateB = dayjs(b.created_at);
      return dateB.isBefore(dateA) ? -1 : 1;
    });

  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async () => {
    try {
      const response = await dispatch(
        FetchMasterGiftAction(userInfo.access_token, { download: true }),
      );
      const fileUrl = response?.download_url;
      if (fileUrl) {
        downloadFile(fileUrl);
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleEditScheme = (scheme) => {
    navigate("/dashboard/creategift", { state: { scheme } });
  };

  const handleAddGiftToScheme = (scheme) => {
    navigate("/dashboard/add-gift-to-scheme", {
      state: { scheme },
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(FetchMasterGiftAction(userInfo.access_token));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mt-2 max-w-full mx-auto rounded-lg p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600 ">Gift Schemes</h2>
          </div>

          {canEdit && (
            <button
              className="flex-1 sm:flex-none bg-white flex items-center justify-center px-3 py-2 text-xs sm:text-sm font-medium text-black border border-gray-500 rounded-md hover:bg-gray-50 transition"
              onClick={() => navigate("/dashboard/creategift")}
            >
              <span className="hidden sm:inline">Add Gift Master</span>
              <span className="sm:hidden">Add</span>
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center mb-4 justify-between gap-4">
          <div className="w-full md:w-2/3 flex flex-wrap gap-4 items-center">
            <SearchBar
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search by Scheme Name / Created By"
            />

            <div className="relative w-full md:w-1/3">
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                isClearable
                placeholderText="Select date range"
                dateFormat="dd MMM yyyy"
                className="placeholder-gray-500 w-full px-4 py-2 border border-gray-500 rounded-md text-sm"
              />
            </div>
          </div>
        </div>

        {loading || refreshing ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FaSpinner className="animate-spin text-amber-500 text-4xl mb-4" />
            <p className="font-bold text-md text-center text-gray-950">
              {refreshing ? "Refreshing..." : "Loading schemes..."}
            </p>
          </div>
        ) : filteredSchemes.length === 0 ? (
          <p className="font-bold text-md text-center text-gray-950 py-6">
            No schemes found
          </p>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-300">
              <table className="w-full bg-white">
                <thead className="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Scheme Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Gift Count
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Created By
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Created Date
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchemes.map((scheme) => (
                    <React.Fragment key={scheme.scheme_name}>
                      <tr className="hover:bg-blue-50 border-b border-gray-300">
                        <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                          {scheme.scheme_name}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                            {scheme.gift_count}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {scheme.gifts?.[0]?.created_by || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {dayjs(scheme.created_at).format("DD MMM YYYY")}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() =>
                                setExpandedScheme(
                                  expandedScheme === scheme.scheme_name
                                    ? null
                                    : scheme.scheme_name,
                                )
                              }
                              className="inline-flex items-center px-3 py-2 text-sm font-medium bg-amber-500 text-white rounded-md hover:bg-amber-600 transition"
                            >
                              {expandedScheme === scheme.scheme_name ? (
                                <>
                                  <FaChevronUp className="mr-2" size={14} />
                                  Hide
                                </>
                              ) : (
                                <>
                                  <FaChevronDown className="mr-2" size={14} />
                                  View
                                </>
                              )}
                            </button>
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleAddGiftToScheme(scheme)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium bg-green-500 text-white rounded-md hover:bg-green-600 transition"
                                  title="Add Gift to Scheme"
                                >
                                  <FaPlus className="mr-2" size={14} />
                                  Add Gift
                                </button>
                                <button
                                  onClick={() => handleEditScheme(scheme)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                                  title="Edit Gift Master"
                                >
                                  <FaEdit className="mr-2" size={14} />
                                  Edit
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {expandedScheme === scheme.scheme_name && (
                        <tr>
                          <td colSpan="5" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <h4 className="font-semibold text-gray-900 mb-3">
                                Gifts in this scheme (
                                {scheme.gifts?.length || 0})
                              </h4>
                              {scheme.gifts && scheme.gifts.length > 0 ? (
                                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                                  <table className="w-full bg-white text-sm">
                                    <thead className="bg-gray-200 border-b border-gray-300">
                                      <tr>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          Gift Image
                                        </th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          Gift Name
                                        </th>
                                        {/* <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          QR Count
                                        </th> */}
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          {isLoyaltyUser
                                            ? "QR Count"
                                            : "Points"}
                                        </th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          Bumper Offer
                                        </th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          Status
                                        </th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          Expired At
                                        </th>
                                        {/* {canEdit && <th className="px-4 py-2 text-center font-semibold text-gray-700">Edit</th>} */}
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">
                                          Created Date
                                        </th>
                                        
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {scheme.gifts.map((gift) => (
                                        <tr
                                          key={gift.gift_id}
                                          className="border-b border-gray-300 hover:bg-gray-100"
                                        >
                                          <td className="px-4 py-3">
                                            {gift.gift_image ? (
                                              <img
                                                src={getImageUrl(
                                                  gift.gift_image,
                                                )}
                                                alt={gift.gift_name}
                                                className="w-12 h-12 rounded object-cover border border-gray-300"
                                                onError={(e) => {
                                                  e.target.src =
                                                    'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><rect width="48" height="48" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="10">No Image</text></svg>';
                                                }}
                                              />
                                            ) : (
                                              <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                                <FaGift className="text-gray-400" />
                                              </div>
                                            )}
                                          </td>
                                          <td className="px-4 py-3 text-gray-900 font-medium">
                                            {gift.gift_name}
                                          </td>
                                          <td className="px-4 py-3 text-gray-900">
                                            {/* <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700">
                                              {gift.qr_count || 0}
                                            </span> */}
                                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700">
                                              {isLoyaltyUser
                                                ? gift.qr_count || 0
                                                : gift.qr_count || 0}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            {gift.is_additional_gift ? (
                                              <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                                                Yes
                                              </span>
                                            ) : (
                                              <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600">
                                                No
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-4 py-3">
                                            <span
                                              className={`px-3 py-1 rounded text-xs font-semibold ${
                                                gift.is_live
                                                  ? "bg-green-100 text-green-800"
                                                  : "bg-red-100 text-red-800"
                                              }`}
                                            >
                                              {gift.is_live
                                                ? "Live"
                                                : "Inactive"}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-900">
                                            {gift.expired_at ? (
                                              <span className="text-sm">
                                                {dayjs(gift.expired_at).format(
                                                  "DD MMM YYYY",
                                                )}
                                              </span>
                                            ) : (
                                              <span className="text-sm font-medium text-blue-600">
                                                Lifetime
                                              </span>
                                            )}
                                          </td>
                                          {/* {canEdit && (
                                            <td className="px-4 py-3 text-center">
                                              {gift.is_live ? (
                                                <button
                                                  onClick={() =>
                                                    handleEditScheme(scheme)
                                                  }
                                                  className="inline-flex items-center px-3 py-2 text-sm font-medium bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                                                >
                                                  <FaEdit
                                                    className="mr-1"
                                                    size={14}
                                                  />
                                                  Edit
                                                </button>
                                              ) : (
                                                <span className="text-gray-400 text-sm">-</span>
                                              )}
                                            </td>
                                          )} */}
                                          <td className="px-4 py-3 text-gray-900">
                                            {dayjs(gift.created_at).format(
                                              "DD MMM YYYY",
                                            )}
                                          </td>
                                          
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-gray-500 text-sm">
                                  No gifts found for this scheme.
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {filteredSchemes.map((scheme) => (
                <div
                  key={scheme.scheme_name}
                  className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden"
                >
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {scheme.scheme_name}
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              Gift Count:
                            </span>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                              {scheme.gift_count}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              Created By:
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {scheme.gifts?.[0]?.created_by || "-"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              Created Date:
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {dayjs(scheme.created_at).format("DD MMM YYYY")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() =>
                          setExpandedScheme(
                            expandedScheme === scheme.scheme_name
                              ? null
                              : scheme.scheme_name,
                          )
                        }
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium bg-amber-500 text-white rounded-md hover:bg-amber-600 transition"
                      >
                        {expandedScheme === scheme.scheme_name ? (
                          <>
                            <FaChevronUp className="mr-2" size={14} />
                            Hide
                          </>
                        ) : (
                          <>
                            <FaChevronDown className="mr-2" size={14} />
                            View
                          </>
                        )}
                      </button>
                      {canEdit && (
                        <>
                          <button
                            onClick={() => handleAddGiftToScheme(scheme)}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium bg-green-500 text-white rounded-md hover:bg-green-600 transition"
                          >
                            <FaPlus className="mr-2" size={14} />
                            Add Gift
                          </button>
                          <button
                            onClick={() => handleEditScheme(scheme)}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                          >
                            <FaEdit className="mr-2" size={14} />
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {expandedScheme === scheme.scheme_name && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        Gifts in this scheme ({scheme.gifts?.length || 0})
                      </h4>
                      {scheme.gifts && scheme.gifts.length > 0 ? (
                        <div className="space-y-3">
                          {scheme.gifts.map((gift) => (
                            <div
                              key={gift.gift_id}
                              className="bg-white border border-gray-200 rounded-lg p-3 space-y-2"
                            >
                              <div className="flex items-start gap-3">
                                {gift.gift_image ? (
                                  <img
                                    src={getImageUrl(gift.gift_image)}
                                    alt={gift.gift_name}
                                    className="w-16 h-16 rounded object-cover border border-gray-300 flex-shrink-0"
                                    onError={(e) => {
                                      e.target.src =
                                        'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">No Image</text></svg>';
                                    }}
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                    <FaGift
                                      className="text-gray-400"
                                      size={24}
                                    />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-semibold text-gray-900 mb-2 truncate">
                                    {gift.gift_name}
                                  </h5>
                                  <div className="space-y-1.5 text-sm">
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600">
                                        QR Count:
                                      </span>
                                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700">
                                        {gift.qr_count || 0}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600">
                                        Bumper Offer:
                                      </span>
                                      {gift.is_additional_gift ? (
                                        <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                                          Yes
                                        </span>
                                      ) : (
                                        <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600">
                                          No
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600">
                                        Status:
                                      </span>
                                      <span
                                        className={`px-3 py-1 rounded text-xs font-semibold ${
                                          gift.is_live
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {gift.is_live ? "Live" : "Inactive"}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600">
                                        Expired At:
                                      </span>
                                      {gift.expired_at ? (
                                        <span className="text-xs text-gray-900">
                                          {dayjs(gift.expired_at).format(
                                            "DD MMM YYYY",
                                          )}
                                        </span>
                                      ) : (
                                        <span className="text-xs font-medium text-blue-600">
                                          Lifetime
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600">
                                        Created Date:
                                      </span>
                                      <span className="text-xs text-gray-900">
                                        {dayjs(gift.created_at).format(
                                          "DD MMM YYYY",
                                        )}
                                      </span>
                                    </div>
                                    {canEdit && gift.is_live && (
                                      <div className="pt-2">
                                        <button
                                          onClick={() =>
                                            handleEditScheme(scheme)
                                          }
                                          className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                                        >
                                          <FaEdit className="mr-2" size={14} />
                                          Edit
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-4">
                          No gifts found for this scheme.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GiftList;
